import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { ApiError, apiFetch } from '../lib/api';
import { Modal } from '../components/Modal';

/** Destinatario del newsletter (no es un usuario del sistema, no tiene login). */
interface Client {
  id: string;
  name: string | null;
  company: string | null;
  email: string;
  sectors: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

/** Sectores sugeridos para clasificar/filtrar clientes (solo UI en v1). */
const SECTORES_SUGERIDOS = [
  'minería',
  'energía',
  'industria',
  'agroindustria',
  'inmobiliario',
  'sanitarias',
  'SEIA',
  'aguas',
  'residuos/REP',
  'aire',
  'ruido',
  'suelos',
  'cierre de faenas',
] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MENSAJES_ERROR: Record<string, string> = {
  email_duplicado: 'Ya existe un cliente con ese correo.',
};

function traducirError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code && MENSAJES_ERROR[err.code]) return MENSAJES_ERROR[err.code];
    return err.message;
  }
  return 'Ocurrió un error inesperado. Inténtalo de nuevo.';
}

const inputClass =
  'mt-2 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] font-normal text-texto outline-none transition-colors duration-150 placeholder:text-gris/60 focus:border-azul-profundo focus:ring-2 focus:ring-azul-profundo/25 disabled:bg-slate-50 disabled:text-gris';
const labelClass = 'block text-xs font-medium uppercase tracking-[0.18em] text-azul-profundo';

function ChipEstado({ activo }: { activo: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
        activo
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : 'bg-slate-100 text-slate-500 ring-slate-200'
      }`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${activo ? 'bg-emerald-500' : 'bg-slate-400'}`}
      />
      {activo ? 'Activo' : 'Inactivo'}
    </span>
  );
}

function ChipSector({ sector }: { sector: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-azul-profundo/10 px-2.5 py-0.5 text-[11px] font-medium text-azul-profundo ring-1 ring-inset ring-azul-profundo/15">
      {sector}
    </span>
  );
}

// ─────────────────────────────── Formulario ────────────────────────────────

function ClienteForm({
  cliente,
  onCerrar,
  onGuardado,
}: {
  cliente: Client | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esEdicion = cliente !== null;
  const [name, setName] = useState(cliente?.name ?? '');
  const [company, setCompany] = useState(cliente?.company ?? '');
  const [email, setEmail] = useState(cliente?.email ?? '');
  const [sectores, setSectores] = useState<string[]>(cliente?.sectors ?? []);
  const [active, setActive] = useState(cliente?.active ?? true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorEmail, setErrorEmail] = useState<string | null>(null);

  const nameId = useId();
  const companyId = useId();
  const emailId = useId();
  const activeId = useId();
  const emailErrId = useId();

  function toggleSector(s: string) {
    setSectores((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    setErrorEmail(null);

    const correo = email.trim();
    if (!EMAIL_RE.test(correo)) {
      setErrorEmail('Ingresa un correo electrónico válido.');
      return;
    }

    setEnviando(true);
    try {
      const body = {
        name: name.trim() || undefined,
        company: company.trim() || undefined,
        email: correo,
        sectors: sectores,
        active,
      };
      if (esEdicion) {
        await apiFetch(`/api/clients/${cliente.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch('/api/clients', { method: 'POST', body: JSON.stringify(body) });
      }
      onGuardado();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_duplicado') {
        setErrorEmail(MENSAJES_ERROR.email_duplicado);
      } else {
        setError(traducirError(err));
      }
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <div>
        <label htmlFor={nameId} className={labelClass}>
          Nombre
        </label>
        <input
          id={nameId}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre de contacto"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={companyId} className={labelClass}>
          Empresa
        </label>
        <input
          id={companyId}
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Razón social o empresa"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor={emailId} className={labelClass}>
          Correo electrónico
        </label>
        <input
          id={emailId}
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errorEmail) setErrorEmail(null);
          }}
          placeholder="destinatario@empresa.cl"
          aria-invalid={errorEmail ? true : undefined}
          aria-describedby={errorEmail ? emailErrId : undefined}
          className={inputClass}
        />
        {errorEmail && (
          <p id={emailErrId} className="mt-1.5 text-sm text-red-700">
            {errorEmail}
          </p>
        )}
      </div>

      <div>
        <span className={labelClass}>Sectores</span>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {SECTORES_SUGERIDOS.map((s) => {
            const activo = sectores.includes(s);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={activo}
                onClick={() => toggleSector(s)}
                className={`rounded-full border px-3 py-1 text-[12px] font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-azul-profundo ${
                  activo
                    ? 'border-azul-profundo bg-azul-profundo/10 text-azul-profundo'
                    : 'border-slate-300 text-gris hover:border-azul-medio hover:text-azul-profundo'
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <label htmlFor={activeId} className="flex items-center gap-3">
        <input
          id={activeId}
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-azul-profundo focus:ring-azul-profundo"
        />
        <span className="text-sm font-normal text-texto">Cliente activo</span>
      </label>

      <div className="mt-2 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-lg px-4 py-2.5 text-sm font-medium text-gris transition-colors duration-150 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={enviando}
          className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
        >
          {enviando ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Crear cliente'}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────── Confirmación de eliminación ───────────────────────

function ConfirmarEliminar({
  cliente,
  onCerrar,
  onEliminado,
}: {
  cliente: Client;
  onCerrar: () => void;
  onEliminado: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onEliminar() {
    setError(null);
    setEnviando(true);
    try {
      await apiFetch(`/api/clients/${cliente.id}`, { method: 'DELETE' });
      onEliminado();
    } catch (err) {
      setError(traducirError(err));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Eliminar cliente" onCerrar={onCerrar}>
      {error && (
        <div
          role="alert"
          className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}
      <p className="text-sm font-light leading-relaxed text-texto">
        ¿Seguro que quieres eliminar a{' '}
        <span className="font-medium text-azul-profundo">
          {cliente.name || cliente.company || cliente.email}
        </span>
        ? Dejará de recibir el newsletter. Esta acción no se puede deshacer.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-lg px-4 py-2.5 text-sm font-medium text-gris transition-colors duration-150 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onEliminar}
          disabled={enviando}
          className="rounded-lg bg-red-600 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {enviando ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </Modal>
  );
}

// ──────────────────────────────── Página ───────────────────────────────────

type FiltroEstado = 'todos' | 'activos' | 'inactivos';

type Dialogo =
  | { tipo: 'crear' }
  | { tipo: 'editar'; cliente: Client }
  | { tipo: 'eliminar'; cliente: Client }
  | null;

// ── Importación masiva por CSV ───────────────────────────────────────────────

const PLANTILLA_CSV =
  '﻿nombre,empresa,email,sectores\n' +
  '"María Pérez","Minera Andina SpA","maria.perez@empresa.cl","minería | energía"\n' +
  '"Juan Soto","Agroexport Ltda.","juan.soto@agro.cl","agroindustria"\n' +
  '"","Sanitaria del Sur","contacto@sanitariasur.cl",""\n';

function descargarPlantilla() {
  const blob = new Blob([PLANTILLA_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'AMBEC_Plantilla_Clientes.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/** Parser CSV propio: BOM, comillas con escape "" y delimitador , o ; (Excel chileno). */
function parsearCsv(crudo: string): string[][] {
  const texto = crudo.replace(/^﻿/, '');
  const finPrimera = texto.indexOf('\n');
  const primera = finPrimera === -1 ? texto : texto.slice(0, finPrimera);
  const delim = (primera.match(/;/g)?.length ?? 0) > (primera.match(/,/g)?.length ?? 0) ? ';' : ',';
  const filas: string[][] = [];
  let fila: string[] = [];
  let campo = '';
  let enComillas = false;
  for (let i = 0; i < texto.length; i++) {
    const ch = texto[i]!;
    if (enComillas) {
      if (ch === '"') {
        if (texto[i + 1] === '"') { campo += '"'; i++; }
        else enComillas = false;
      } else campo += ch;
    } else if (ch === '"') enComillas = true;
    else if (ch === delim) { fila.push(campo); campo = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && texto[i + 1] === '\n') i++;
      fila.push(campo); campo = '';
      if (fila.some((c) => c.trim() !== '')) filas.push(fila);
      fila = [];
    } else campo += ch;
  }
  fila.push(campo);
  if (fila.some((c) => c.trim() !== '')) filas.push(fila);
  return filas;
}

interface FilaImport {
  linea: number;
  name: string | null;
  company: string | null;
  email: string;
  sectors: string[];
  error: string | null;
}

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/** Mapea el CSV a filas validadas. El encabezado acepta sinónimos (nombre/name, correo/email…). */
function filasDesdeCsv(crudo: string): { filas: FilaImport[]; errorGlobal: string | null } {
  const tabla = parsearCsv(crudo);
  if (tabla.length < 2) return { filas: [], errorGlobal: 'El archivo no tiene filas de datos (usa la plantilla como guía).' };
  const encabezado = tabla[0]!.map(norm);
  const col = (...alias: string[]) => encabezado.findIndex((h) => alias.includes(h));
  const iEmail = col('email', 'correo', 'e-mail', 'mail');
  if (iEmail === -1) return { filas: [], errorGlobal: 'Falta la columna "email" en el encabezado (descarga la plantilla).' };
  const iNombre = col('nombre', 'name');
  const iEmpresa = col('empresa', 'company', 'organizacion', 'organización');
  const iSectores = col('sectores', 'sectors', 'sector');

  const filas: FilaImport[] = tabla.slice(1).map((f, idx) => {
    const email = (f[iEmail] ?? '').trim();
    const error = !email
      ? 'Sin email'
      : !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)
        ? 'Email inválido'
        : null;
    return {
      linea: idx + 2,
      name: iNombre >= 0 ? (f[iNombre] ?? '').trim() || null : null,
      company: iEmpresa >= 0 ? (f[iEmpresa] ?? '').trim() || null : null,
      email,
      sectors: iSectores >= 0 ? (f[iSectores] ?? '').split(/[|/]/).map((s) => s.trim()).filter(Boolean) : [],
      error,
    };
  });
  return { filas, errorGlobal: null };
}

interface ResultadoImport {
  creados: number;
  ya_existian: number;
  duplicados_en_archivo: number;
  invalidas: number;
}

function ImportarCsv({ onCerrar, onImportado }: { onCerrar: () => void; onImportado: () => void }) {
  const [filas, setFilas] = useState<FilaImport[]>([]);
  const [nombreArchivo, setNombreArchivo] = useState<string | null>(null);
  const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImport | null>(null);
  const inputId = useId();

  async function alElegirArchivo(archivo: File | null) {
    setErrorArchivo(null);
    setFilas([]);
    setResultado(null);
    if (!archivo) return;
    setNombreArchivo(archivo.name);
    const { filas: fs, errorGlobal } = filasDesdeCsv(await archivo.text());
    if (errorGlobal) { setErrorArchivo(errorGlobal); return; }
    if (fs.length > 500) { setErrorArchivo(`El archivo trae ${fs.length} filas; el máximo por importación es 500.`); return; }
    setFilas(fs);
  }

  const validas = filas.filter((f) => !f.error);

  async function importar() {
    setImportando(true);
    setErrorArchivo(null);
    try {
      const r = await apiFetch<Omit<ResultadoImport, 'invalidas'>>('/api/clients/import', {
        method: 'POST',
        body: JSON.stringify({ clientes: validas.map(({ name, company, email, sectors }) => ({ name, company, email, sectors })) }),
      });
      setResultado({ ...r, invalidas: filas.length - validas.length });
      onImportado();
    } catch (err) {
      setErrorArchivo(traducirError(err));
    } finally {
      setImportando(false);
    }
  }

  return (
    <Modal titulo="Importar clientes desde CSV" onCerrar={onCerrar} bloqueante ancho="xl">
      {resultado ? (
        <>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
            <p className="text-sm font-medium text-emerald-900">Importación completada</p>
            <ul className="mt-2 flex flex-col gap-1 text-sm font-normal text-emerald-900">
              <li>✓ {resultado.creados} {resultado.creados === 1 ? 'cliente nuevo creado' : 'clientes nuevos creados'}</li>
              {resultado.ya_existian > 0 && <li>· {resultado.ya_existian} {resultado.ya_existian === 1 ? 'ya existía (no se modificó)' : 'ya existían (no se modificaron)'}</li>}
              {resultado.duplicados_en_archivo > 0 && <li>· {resultado.duplicados_en_archivo} {resultado.duplicados_en_archivo === 1 ? 'fila repetida dentro del archivo (se tomó la primera)' : 'filas repetidas dentro del archivo (se tomó la primera de cada una)'}</li>}
              {resultado.invalidas > 0 && <li className="text-amber-800">· {resultado.invalidas} {resultado.invalidas === 1 ? 'fila inválida no se importó' : 'filas inválidas no se importaron'}</li>}
            </ul>
          </div>
          <div className="mt-5 flex justify-end">
            <button type="button" onClick={onCerrar} className="btn-primario">Listo</button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm font-normal leading-relaxed text-texto">
            Sube un archivo CSV con columnas <span className="font-medium">nombre, empresa, email, sectores</span> (solo
            email es obligatorio; sectores separados por <code className="rounded bg-slate-100 px-1">|</code>). Acepta coma o
            punto y coma como separador. Los correos que ya existan no se tocan.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button type="button" onClick={descargarPlantilla} className="btn-secundario">
              Descargar plantilla CSV
            </button>
            <label htmlFor={inputId} className="btn-primario cursor-pointer">
              {nombreArchivo ? 'Elegir otro archivo' : 'Elegir archivo…'}
            </label>
            <input
              id={inputId}
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => void alElegirArchivo(e.target.files?.[0] ?? null)}
            />
            {nombreArchivo && <span className="text-xs font-normal text-gris">{nombreArchivo}</span>}
          </div>

          {errorArchivo && <p role="alert" className="alerta-error mt-4">{errorArchivo}</p>}

          {filas.length > 0 && (
            <>
              <div className="mt-4 max-h-[38vh] overflow-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-[#F8FAFC]">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gris">Fila</th>
                      <th className="px-3 py-2 font-medium text-gris">Nombre</th>
                      <th className="px-3 py-2 font-medium text-gris">Empresa</th>
                      <th className="px-3 py-2 font-medium text-gris">Email</th>
                      <th className="px-3 py-2 font-medium text-gris">Sectores</th>
                      <th className="px-3 py-2 font-medium text-gris">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filas.map((f) => (
                      <tr key={f.linea} className={f.error ? 'bg-red-50/50' : undefined}>
                        <td className="px-3 py-1.5 tabular-nums text-gris">{f.linea}</td>
                        <td className="px-3 py-1.5 text-texto">{f.name ?? '—'}</td>
                        <td className="px-3 py-1.5 text-texto">{f.company ?? '—'}</td>
                        <td className="px-3 py-1.5 text-texto">{f.email || '—'}</td>
                        <td className="px-3 py-1.5 text-gris">{f.sectors.join(', ') || '—'}</td>
                        <td className="px-3 py-1.5">
                          {f.error ? (
                            <span className="chip bg-red-50 text-red-700 ring-red-300">{f.error}</span>
                          ) : (
                            <span className="chip bg-emerald-50 text-emerald-700 ring-emerald-300">OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-normal text-gris">
                  {validas.length} de {filas.length} filas listas para importar
                  {filas.length - validas.length > 0 && ` — ${filas.length - validas.length} con errores quedarán fuera`}.
                </p>
                <div className="flex gap-2">
                  <button type="button" onClick={onCerrar} disabled={importando} className="btn-fantasma">Cancelar</button>
                  <button
                    type="button"
                    onClick={() => void importar()}
                    disabled={importando || validas.length === 0}
                    className="btn-primario disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {importando ? 'Importando…' : `Importar ${validas.length} ${validas.length === 1 ? 'cliente' : 'clientes'}`}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}

export default function Clientes() {
  const [clientes, setClientes] = useState<Client[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [alternando, setAlternando] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('todos');
  const [filtroSector, setFiltroSector] = useState<string>('');
  const [importando, setImportando] = useState(false);

  const filtroEstadoId = useId();
  const filtroSectorId = useId();

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const params = new URLSearchParams();
      if (filtroSector) params.set('sector', filtroSector);
      if (filtroEstado === 'activos') params.set('active', 'true');
      else if (filtroEstado === 'inactivos') params.set('active', 'false');
      const qs = params.toString();
      const data = await apiFetch<{ clients: Client[] }>(
        `/api/clients${qs ? `?${qs}` : ''}`,
      );
      setClientes(data.clients);
    } catch (err) {
      setErrorCarga(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, [filtroEstado, filtroSector]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  function cerrarYRefrescar() {
    setDialogo(null);
    void cargar();
  }

  async function alternarActivo(c: Client) {
    setErrorAccion(null);
    setAlternando(c.id);
    try {
      await apiFetch(`/api/clients/${c.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !c.active }),
      });
      await cargar();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setAlternando(null);
    }
  }

  const hayFiltros = filtroEstado !== 'todos' || filtroSector !== '';
  const selectClass =
    'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-texto outline-none transition-colors duration-150 focus:border-azul-profundo focus:ring-2 focus:ring-azul-profundo/25';

  const totalActivos = useMemo(() => clientes.filter((c) => c.active).length, [clientes]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm font-light text-gris">
          Administra los destinatarios del newsletter. No son usuarios con acceso a la plataforma.
        </p>
        <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setImportando(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:border-azul-profundo/50 hover:bg-celeste/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12M7 10l5 5 5-5" />
            <path d="M4 21h16" />
          </svg>
          Importar CSV
        </button>
        <button
          type="button"
          onClick={() => setDialogo({ tipo: 'crear' })}
          className="inline-flex items-center gap-2 rounded-lg bg-azul-profundo px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Nuevo cliente
        </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col">
          <label
            htmlFor={filtroEstadoId}
            className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-gris"
          >
            Estado
          </label>
          <select
            id={filtroEstadoId}
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as FiltroEstado)}
            className={selectClass}
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor={filtroSectorId}
            className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-gris"
          >
            Sector
          </label>
          <select
            id={filtroSectorId}
            value={filtroSector}
            onChange={(e) => setFiltroSector(e.target.value)}
            className={selectClass}
          >
            <option value="">Todos los sectores</option>
            {SECTORES_SUGERIDOS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {hayFiltros && (
          <button
            type="button"
            onClick={() => {
              setFiltroEstado('todos');
              setFiltroSector('');
            }}
            className="rounded-lg px-3 py-2 text-sm font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
          >
            Limpiar filtros
          </button>
        )}

        {!cargando && !errorCarga && (
          <p className="ml-auto text-xs font-light text-gris">
            {clientes.length} {clientes.length === 1 ? 'cliente' : 'clientes'} · {totalActivos}{' '}
            {totalActivos === 1 ? 'activo' : 'activos'}
          </p>
        )}
      </div>

      {errorAccion && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {errorAccion}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {cargando ? (
          <div
            role="status"
            aria-live="polite"
            className="px-6 py-16 text-center text-sm font-light uppercase tracking-[0.2em] text-gris"
          >
            Cargando clientes…
          </div>
        ) : errorCarga ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm text-red-700">{errorCarga}</p>
            <button
              type="button"
              onClick={() => void cargar()}
              className="mt-4 rounded-lg border border-azul-profundo px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
            >
              Reintentar
            </button>
          </div>
        ) : clientes.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-azul-medio">
              {hayFiltros ? 'Sin resultados' : 'Sin clientes todavía'}
            </p>
            <p className="mt-3 text-sm font-light text-gris">
              {hayFiltros
                ? 'Ajusta los filtros para ver más clientes.'
                : 'Agrega tu primer destinatario con “Nuevo cliente”.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-medium uppercase tracking-[0.15em] text-gris">
                  <th scope="col" className="px-6 py-3 font-medium">
                    Cliente
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Sectores
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Estado
                  </th>
                  <th scope="col" className="px-6 py-3 text-right font-medium">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clientes.map((c) => (
                  <tr key={c.id} className="text-sm">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-normal text-texto">{c.name || '—'}</span>
                        {c.company && (
                          <span className="text-xs font-normal text-azul-medio">{c.company}</span>
                        )}
                        <span className="text-xs font-light text-gris">{c.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {c.sectors.length === 0 ? (
                        <span className="text-xs font-light text-gris">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {c.sectors.map((s) => (
                            <ChipSector key={s} sector={s} />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <ChipEstado activo={c.active} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setDialogo({ tipo: 'editar', cliente: c })}
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => void alternarActivo(c)}
                          disabled={alternando === c.id}
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-profundo focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {alternando === c.id ? '…' : c.active ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDialogo({ tipo: 'eliminar', cliente: c })}
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(dialogo?.tipo === 'crear' || dialogo?.tipo === 'editar') && (
        <Modal
          titulo={dialogo.tipo === 'crear' ? 'Nuevo cliente' : 'Editar cliente'}
          onCerrar={() => setDialogo(null)}
        >
          <ClienteForm
            cliente={dialogo.tipo === 'editar' ? dialogo.cliente : null}
            onCerrar={() => setDialogo(null)}
            onGuardado={cerrarYRefrescar}
          />
        </Modal>
      )}

      {importando && (
        <ImportarCsv onCerrar={() => setImportando(false)} onImportado={() => void cargar()} />
      )}

      {dialogo?.tipo === 'eliminar' && (
        <ConfirmarEliminar
          cliente={dialogo.cliente}
          onCerrar={() => setDialogo(null)}
          onEliminado={cerrarYRefrescar}
        />
      )}
    </div>
  );
}
